'use client';

import { Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from 'sonner';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';

interface ContactUsLinkProps {
  email: string;
  label: string;
  className?: string;
}

/** Opens a popover with the support address: write from the mail app or copy it. */
export function ContactUsLink({ email, label, className }: ContactUsLinkProps) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('footer.contactEmail');
  const tClipboard = useTranslations('toasts.clipboard');

  const close = () => setIsOpen(false);

  // On failure the popover stays open so the address can still be selected by hand.
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(tClipboard('emailCopied'));
      close();
    } catch {
      toast.error(tClipboard('copyFailed'));
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn('w-fit cursor-pointer text-left', className)}>
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[300px] max-w-[calc(100vw-32px)] flex-col gap-3 rounded-[12px] border-v2-border bg-v2-surface-secondary p-4 shadow-[0px_12px_32px_0px_rgba(0,0,0,0.12)] dark:border-white/10 dark:shadow-[0px_12px_32px_0px_rgba(0,0,0,0.4)]"
      >
        <div className="flex flex-col gap-1">
          <p className="text-[12px] leading-[16px] text-v2-text-secondary">{t('hint')}</p>
          <span className="select-all break-all text-[14px] font-semibold leading-5 text-v2-text-primary">
            {email}
          </span>
        </div>
        {/* Wraps to one button per row when a locale's labels don't fit side by side. */}
        <div className="flex flex-wrap gap-2">
          <Button asChild appearance="accent" size="S" className="flex-1">
            <a href={`mailto:${email}`} onClick={close}>
              <Mail className="size-4" />
              {t('write')}
            </a>
          </Button>
          {/* secondary-2: in dark the neutral fill equals the popover surface. */}
          <Button
            appearance="neutral"
            size="S"
            className="flex-1 bg-v2-btn-secondary-2"
            leftIcon={<Icon name="Copy" size={16} />}
            onClick={handleCopy}
          >
            {t('copy')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
